/**
 * Modale "Détails de l'action recommandée".
 *
 * Affiche le contexte d'une action proposée (pourquoi, historique, recommandation,
 * meilleur moment) puis propose un CTA "Passer à la relance" qui ouvre la modale Relance.
 */
import React from 'react';
import { Dialog, DialogContent } from '../../ui/dialog';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import {
  X, Phone, Sparkles, Clock, CheckCircle2, AlertCircle, Send,
} from 'lucide-react';
import { useClientActions } from './ClientActionsContext';
import {
  PRIORITY_BADGE, PRIORITY_LABEL, fmtFcfaFull, fmtDateFr, daysSince,
} from './shared';

function Section({ icon: Icon, color = 'text-blue-500', title, children }) {
  return (
    <div className="border border-gray-100 rounded-lg p-4 bg-white">
      <div className="flex items-center gap-2 mb-2">
        <Icon className={`w-4 h-4 ${color}`} />
        <h3 className="text-sm font-semibold text-gray-900">{title}</h3>
      </div>
      <div className="text-xs text-gray-700 space-y-1">{children}</div>
    </div>
  );
}

export default function RecommendedActionModal() {
  const { modal, client, action, close, switchModal } = useClientActions();
  const isOpen = modal === 'recommended' && !!client;

  if (!isOpen) return null;

  const priority = action?.priority || 'haute';
  const days = action?.recommendedSinceDays ?? daysSince(client.lastSeen) ?? 9;

  return (
    <Dialog open={isOpen} onOpenChange={(o) => !o && close()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-hidden flex flex-col p-0 gap-0" data-testid="recommended-action-modal">
        {/* Header */}
        <div className="px-6 pt-6 pb-4 flex items-start justify-between gap-4">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center">
              <Phone className="w-5 h-5 text-emerald-600" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Détails de l'action recommandée</h2>
              <p className="text-xs text-gray-500">Plus d'informations pour vous aider à agir efficacement.</p>
            </div>
          </div>
          <button onClick={close} className="text-gray-400 hover:text-gray-600" data-testid="close-rec-btn">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="overflow-auto flex-1 px-6 pb-6 space-y-3">
          {/* Carte résumé */}
          <div className="border border-gray-100 rounded-lg p-4 flex items-start justify-between gap-3">
            <div className="flex items-start gap-3 min-w-0">
              <div className="w-10 h-10 rounded-lg bg-red-50 flex items-center justify-center shrink-0">
                <Phone className="w-5 h-5 text-red-500" />
              </div>
              <div className="min-w-0">
                <h3 className="text-sm font-semibold text-gray-900">{action?.title || 'Relancer un client inactif'}</h3>
                <p className="text-xs text-gray-500 mt-0.5">
                  {client.companyName} • {client.primaryEmail || '—'}
                </p>
                <p className="text-[11px] text-gray-400 mt-1">Recommandé depuis {days} jour{days > 1 ? 's' : ''}</p>
              </div>
            </div>
            <Badge className={`${PRIORITY_BADGE[priority]} border text-[10px] shrink-0`}>
              {PRIORITY_LABEL[priority] || priority}
            </Badge>
          </div>

          {/* Pourquoi */}
          <Section icon={Sparkles} color="text-violet-500" title="Pourquoi cette action ?">
            <p>
              Ce client est inactif depuis {days} jour{days > 1 ? 's' : ''}. Les données montrent que les clients inactifs
              ont 40% moins de chances de repasser commande si aucune action n'est effectuée dans les 14 jours.
            </p>
          </Section>

          {/* Historique */}
          <Section icon={Clock} color="text-blue-500" title="Historique du client">
            <Line label="Dernière activité" value={fmtDateFr(client.lastSeen) || '—'} />
            <Line label="Nombre total de demandes" value={client.ordersCount ?? 7} />
            <Line label="Montant total des commandes" value={fmtFcfaFull(client.totalSpend ?? 2450000)} />
            <Line label="Panier moyen" value={fmtFcfaFull(client.avgOrderValue ?? 350000)} />
          </Section>

          {/* Recommandation */}
          <Section icon={CheckCircle2} color="text-emerald-500" title="Recommandation">
            <p>
              Relancez ce client avec une offre personnalisée pour réactiver la relation.
              Proposez-lui une solution adaptée à ses derniers besoins de transport.
            </p>
          </Section>

          {/* Meilleur moment */}
          <Section icon={AlertCircle} color="text-amber-500" title="Meilleur moment pour agir">
            <p>Aujourd'hui est le moment idéal pour relancer ce client.</p>
          </Section>
        </div>

        <div className="px-6 py-4 border-t border-gray-100 flex items-center justify-between">
          <Button variant="ghost" size="sm" onClick={close} data-testid="rec-close-btn">Fermer</Button>
          <Button
            size="sm" onClick={() => switchModal('relance')}
            className="bg-emerald-600 hover:bg-emerald-700 text-white"
            data-testid="rec-relance-btn"
          >
            <Send className="w-3.5 h-3.5 mr-1.5" /> Passer à la relance
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Line({ label, value }) {
  return (
    <div className="flex items-center justify-between gap-3 py-1">
      <span className="text-gray-500">{label}</span>
      <span className="text-gray-900 font-medium">{value}</span>
    </div>
  );
}
